import { HTMLAttributes, forwardRef, ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface PageContainerProps extends HTMLAttributes<HTMLDivElement> {
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full';
  centered?: boolean;
  padded?: boolean;
  title?: string;
  description?: string;
  icon?: LucideIcon;
  actions?: ReactNode;
}

const maxWidthClasses: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  full: 'max-w-full',
};

const PageContainer = forwardRef<HTMLDivElement, PageContainerProps>(
  ({ className, maxWidth = '7xl', centered = true, padded = true, title, description, icon: Icon, actions, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          maxWidthClasses[maxWidth],
          centered && 'mx-auto',
          padded && 'px-4 sm:px-6 lg:px-8',
          className
        )}
        {...props}
      >
        {(title || description || Icon || actions) && (
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {Icon && <Icon className="w-7 h-7 text-primary-400" />}
              <div>
                {title && <h1 className="text-2xl font-bold text-surface-100">{title}</h1>}
                {description && <p className="text-surface-400 mt-1">{description}</p>}
              </div>
            </div>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
          </div>
        )}
        {children}
      </div>
    );
  }
);

PageContainer.displayName = 'PageContainer';

export { PageContainer };
